import ProgressBar from "./progressBar";
import Toggle from "./toggle";
import Button from "./button";
import usaStates from "../../usaStates";
import React, { useEffect, useState, useRef, memo, forwardRef } from "react";
import { RestaurantsService } from "../../services/Restaurants";
import { Autocomplete, TextField } from "@mui/material";
import { X } from "lucide-react";

// Stable simple Label (pure, cheap)
export const Label = ({ children }) => (
  <label className="block text-[14px] font-medium text-gray-500 mb-1  tracking-wide">
    {children}
  </label>
);

// Memoized, forwardRef Input to prevent remounts and keep focus stable
export const Input = memo(
  forwardRef(function InputComponent(props, ref) {
    return (
      <input
        ref={ref}
        {...props}
        className="w-full bg-white border border-gray-200 rounded-lg p-2.5 text-[13px] font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#881337]/10 focus:border-[#881337] transition-all placeholder:text-gray-300"
      />
    );
  })
);


const StaffListModal = ({ isOpen, onClose, users = [] }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 animate-in fade-in zoom-in-95">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-800">Staff Overview</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-5 max-h-[60vh] overflow-y-auto space-y-4">
          {users.length === 0 ? (
            <p className="text-sm text-gray-400 text-center">
              No staff assigned
            </p>
          ) : (
            users.map((user) => (
              <div
                key={user.uuid}
                className="flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-gray-300 overflow-hidden" >
                    <div className="w-full h-full bg-[#374151] flex items-center justify-center">
                      <span className="text-[14px] text-white">
                        {user.first_name.charAt(0)}
                        {user.last_name.charAt(0)}
                      </span>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">
                      {user.first_name} {user.last_name}
                    </p>
                    <p className="text-xs text-gray-400">{user.email}</p>
                  </div>
                </div>

                <span className="bg-[#FCE7E7] text-[#881337] text-xs font-medium px-3 py-1 rounded-full">
                  {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                </span>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t">
          <button
            onClick={onClose}
            className="w-full py-2 text-sm font-semibold rounded-lg bg-[#881337] text-white hover:bg-[#6f0f2b]"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default function ManageRestaurantPanel({
  restaurant,
  onClose,
  fetchData,
}) {
  const [showManagersModal, setShowManagersModal] = useState(false);
  const [showEmployeesModal, setShowEmployeesModal] = useState(false);
  const [restaurantName, setRestaurantName] = useState(restaurant?.name || "");
  const [addressStreet, setAddressStreet] = useState(
    restaurant?.address?.street || ""
  );
  const [addressCity, setAddressCity] = useState(
    restaurant?.address?.city || ""
  );
  const [addressState, setAddressState] = useState(
    restaurant?.address?.state || ""
  );
  const [addressZip, setAddressZip] = useState(restaurant?.address?.zip || "");
  const [phoneNumber, setPhoneNumber] = useState(restaurant?.phone || "");
  const [usersAssigned, setUsersAssigned] = useState(
    [...restaurant?.managers, ...restaurant?.employees] || []
  );
  const [directorsAssigned, setDirectorsAssigned] = useState(
    restaurant?.directors || []
  );
  const [managersAssigned, setManagersAssigned] = useState(
    restaurant?.managers || []
  );
  const [employeesAssigned, setEmployeesAssigned] = useState(
    restaurant?.employees || []
  );
  const [restaurantStatus, setRestaurantStatus] = useState(
    restaurant.status === "active" ? true : false
  );
  const [isSaving, setIsSaving] = useState(false);
  const cuisineRef = useRef("");
  const userRole = localStorage.getItem("userRole");

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  // Search data for managers / employees
  const [searchData, setSearchData] = useState({ managers: [], employees: [] });
  const [loadingSearch, setLoadingSearch] = useState(false);

  useEffect(() => {
    const fetchSearchData = async () => {
      setLoadingSearch(true);
      try {
        const data = await RestaurantsService.searchData();
        setSearchData(data);
      } catch (err) {
        console.error("Error fetching search data:", err);
      } finally {
        setLoadingSearch(false);
      }
    };
    fetchSearchData();
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await RestaurantsService.updateRestaurant(restaurant.uuid, {
        name: restaurantName,
        address: {
          street: addressStreet,
          city: addressCity,
          state: addressState,
          zip: addressZip,
        },
        status: restaurantStatus ? "active" : "inactive",
        cuisine_type: cuisineRef.current.value,
        phone: phoneNumber,
        directors: directorsAssigned.map((dir) => dir.uuid),
        managers: managersAssigned.map((mgr) => mgr.uuid),
        employees: employeesAssigned.map((emp) => emp.uuid),
      });
      onClose();
      fetchData();
    } catch (error) {
      console.error("Failed to update restaurant", error);
    } finally {
      setIsSaving(false);
    }
  };



  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "phone") {
      const digits = value.replace(/\D/g, "").slice(0, 10); // Only digits, max 10
      let formatted = digits;

      if (digits.length > 6) {
        formatted = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
      } else if (digits.length > 3) {
        formatted = `${digits.slice(0, 3)}-${digits.slice(3)}`;
      }

      setPhoneNumber(formatted);
      // return;
    }


  };

  return (
    <div className="fixed inset-y-0 text-start right-0 w-full md:w-[500px] lg:w-[500px] bg-[#FFFAF2] border-l border-gray-200 shadow-2xl z-50 overflow-y-auto ">
      {/* Header */}
      <div className="p-6 pb-4 flex justify-between items-start">
        <h2 className="text-lg font-bold text-[#1F2937] leading-[1.2] max-w-[280px]">
          Manage Restaurant — {restaurant?.name}
        </h2>
        <button
          onClick={onClose}
          className="p-1 hover:bg-gray-100 rounded-full transition-colors"
        >
          <X className="w-4 h-4 text-gray-400" />
        </button>
      </div>

      <div className="px-6 pb-8 space-y-8">
        {/* Restaurant Information Section */}
        <div className="space-y-2">
          <h3 className="text-m font-bold  text-[#1F2937]">
            Restaurant Information
          </h3>

          <div className="space-y-2 text-l">
            <div>
              <Label>Restaurant Name *</Label>
              <Input
                type="text"
                value={restaurantName}
                onChange={(e) => setRestaurantName(e.target.value)}
                placeholder="Restaurant Name"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Street Address</Label>
                <Input
                  type="text"
                  value={addressStreet}
                  onChange={(e) => setAddressStreet(e.target.value)}
                  placeholder="Street"
                />
              </div>
              <div>
                <Label>City</Label>
                <Input
                  type="text"
                  value={addressCity}
                  onChange={(e) => setAddressCity(e.target.value)}
                  placeholder="City"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>State</Label>
                <select
                  value={addressState}
                  onChange={(e) => setAddressState(e.target.value)}
                  className="w-full bg-white border border-gray-200 rounded-lg p-2.5 text-[13px] font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#881337]/10 focus:border-[#881337] transition-all appearance-none cursor-pointer"
                >
                  <option value="">Select State</option>
                  {Object.entries(usaStates).map(([code, name]) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label>ZIP Code</Label>
                <Input
                  type="text"
                  value={addressZip}
                  maxLength={5}
                  onChange={(e) => setAddressZip(e.target.value)}
                  placeholder="ZIP"
                />
              </div>
            </div>

            <div>
              <Label>Cuisine Type</Label>
              <Input
                type="text"
                defaultValue={restaurant?.cuisine_type || ""}
                ref={cuisineRef}
                placeholder="Cuisine Type"
              />
            </div>

            <div>
              <Label>Phone</Label>
              <Input
                value={phoneNumber}
                onChange={handleInputChange}
                type="text"
                name="phone"
                placeholder="555-123-4567"
              />
            </div>
          </div>
        </div>

        {/* Ownership & Status Section */}
        <div className="space-y-4">
          <h3 className="text-sm  text-[#1F2937]">Ownership & Status</h3>

          <div className="space-y-3">
            <div>
              <Label>Account Owner</Label>
              <div className="bg-[#FEF3C7] border border-orange-100 rounded-lg p-3">
                <p className="text-[13px]  text-[#92400E]">
                  {restaurant?.account_owner?.first_name}{" "}
                  {restaurant?.account_owner?.last_name || "Super Admin"}
                </p>
              </div>
            </div>

            <div>
              <Label>Franchise Status</Label>
              <div className="bg-[#881337] rounded p-1 flex items-center justify-center">
                <p className="text-[14px]  text-white  tracking-wider">
                  Independent Restaurant
                </p>
              </div>
            </div>

            <div>
              <Label>Restaurant Status</Label>
              <div className="flex items-center gap-3">
                <Toggle
                  defaultChecked={restaurantStatus}
                  setRestaurantStatus={setRestaurantStatus}
                />
                <span
                  className={`text-sm font-bold ${restaurantStatus ? "text-emerald-600" : "text-gray-400"
                    }`}
                >
                  {restaurantStatus ? "Active" : "Inactive"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Team Assignments Section */}
        <div className="space-y-2">
          <h3 className="text-lg font-medium text-[#1F2937]">
            Team Assignments
          </h3>

          {/* Directors Assigned */}
          {userRole === "super_admin" && (
            <div className="space-y-1">
              <Label>Directors Assigned</Label>
              <div className="bg-[#FFF9F2]/50 border border-gray-400 rounded-xl p-3 space-y-3             shadow-inner">
                {directorsAssigned.length > 0 &&
                  userRole === "super_admin" &&
                  directorsAssigned.map((director, index) => (
                    <div className="flex flex-wrap gap-2">
                      <div className="flex items-center gap-2 bg-[#F9EAEA] pl-1 pr-3 py-1 rounded-full">
                        <div className="w-6 h-6 rounded-full bg-gray-300 overflow-hidden ring-2 ring-white">
                          <div className="w-full h-full bg-[#525252] flex items-center justify-center">
                            <span className="text-[10px] text-white">
                              {director.first_name.charAt(0)}
                              {director.last_name.charAt(0)}
                            </span>
                          </div>
                        </div>
                        <span className="text-[12px] font-bold text-[#881337]">
                          {director.first_name} {director.last_name}
                        </span>
                        <X
                          onClick={() =>
                            setDirectorsAssigned((prev) =>
                              prev.filter((_, i) => i !== index)
                            )
                          }
                          className="w-3 h-3 text-[#881337] cursor-pointer"
                        />
                      </div>
                    </div>
                  ))}
                <input
                  type="text"
                  placeholder="Search managers..."
                  className="w-full bg-white border border-gray-100 rounded-lg px-3 py-2 text-[12px] font-medium text-gray-400 focus:outline-none placeholder:text-gray-400"
                />
              </div>
            </div>
          )}

          {/* Managers Assigned */}
          {userRole !== "manager" && <div className="space-y-1">
            <Label>Managers Assigned</Label>
            <div className="bg-[#FFF9F2]/50 border border-gray-400 rounded-xl p-3 space-y-3             shadow-inner">
              {managersAssigned.length > 0 &&
                managersAssigned.map((manager, index) => (
                  <div className="flex flex-wrap gap-2">
                    <div className="flex items-center gap-2 bg-[#F9EAEA] pl-1 pr-3 py-1 rounded-full">
                      <div className="w-6 h-6 rounded-full bg-gray-300 overflow-hidden ring-2 ring-white">
                        <div className="w-full h-full bg-[#525252] flex items-center justify-center">
                          <span className="text-[10px] text-white">
                            {manager?.first_name.charAt(0)}
                            {manager?.last_name.charAt(0)}
                          </span>
                        </div>
                      </div>
                      <span className="text-[12px] font-bold text-[#881337]">
                        {manager?.first_name} {manager?.last_name}
                      </span>
                      <X
                        onClick={() =>
                          setManagersAssigned((prev) =>
                            prev.filter((_, i) => i !== index)
                          )
                        }
                        className="w-3 h-3 text-[#881337] cursor-pointer"
                      />
                    </div>
                  </div>
                ))}
              <Autocomplete
                multiple
                options={searchData.managers}
                getOptionLabel={(option) => `${option?.first_name} ${option?.last_name}`}
                value={managersAssigned}
                isOptionEqualToValue={(option, value) => option?.uuid === value.uuid}
                filterSelectedOptions
                onChange={(_, newValue) => setManagersAssigned(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search managers..."
                    className="w-full"
                    size="small"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        const inputValue = params.inputProps.value?.trim().toLowerCase();
                        if (inputValue) {
                          const match = searchData.managers.find(
                            (m) =>
                              `${m.first_name} ${m.last_name}`.toLowerCase() === inputValue
                          );
                          if (match && !managersAssigned.some((a) => a.uuid === match.uuid)) {
                            setManagersAssigned((prev) => [...prev, match]);
                          }
                        }
                      }
                    }}
                  />
                )}
                loading={loadingSearch}
              />
            </div>
          </div>}

          {/* Employees Assigned */}
          <div className="space-y-1.5">
            <Label>Employees Assigned</Label>
            <div className="bg-[#FFF9F2]/50 border border-gray-400 rounded-xl p-3 space-y-3 shadow-inner">
              {employeesAssigned.length > 0 &&
                employeesAssigned.map((employee, index) => (
                  <div className="flex flex-wrap gap-2">
                    <div className="flex items-center gap-2 bg-[#FFF9E5] pl-1 pr-3 py-1 rounded-full">
                      <div className="w-6 h-6 rounded-full bg-gray-300 overflow-hidden ring-2 ring-white">
                        <div className="w-full h-full bg-[#171717] flex items-center justify-center">
                          <span className="text-[10px] text-white">
                            {employee.first_name.charAt(0)}
                            {employee.last_name.charAt(0)}
                          </span>
                        </div>
                      </div>
                      <span className="text-[12px] font-bold text-[#FFC107]">
                        {employee.first_name} {employee.last_name}
                      </span>
                      <X
                        onClick={() =>
                          setEmployeesAssigned((prev) =>
                            prev.filter((_, i) => i !== index)
                          )
                        }
                        className="w-3 h-3 text-[#FFC107] cursor-pointer"
                      />
                    </div>
                  </div>
                ))}
              <Autocomplete
                multiple
                options={searchData.employees}
                getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
                value={employeesAssigned}
                isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
                filterSelectedOptions
                onChange={(_, newValue) => setEmployeesAssigned(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search employees"
                    className="w-full"
                    size="small"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        const inputValue = params.inputProps.value?.trim().toLowerCase();
                        if (inputValue) {
                          const match = searchData.employees.find(
                            (m) =>
                              `${m.first_name} ${m.last_name}`.toLowerCase() === inputValue
                          );
                          if (match && !employeesAssigned.some((a) => a.uuid === match.uuid)) {
                            setEmployeesAssigned((prev) => [...prev, match]);
                          }
                        }
                      }
                    }}
                  />
                )}
                loading={loadingSearch}
              />
            </div>
          </div>

          {/* Staff Overview */}
          <div className="space-y-2 ">
            <Label>Staff Overview</Label>

            <div className="bg-[#F9FAFB] rounded-xl p-3.5 space-y-3">
              {usersAssigned?.length > 0 &&
                usersAssigned?.slice(0, 2).map((user, index) => (
                  <>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-gray-400 overflow-hidden">
                          <div className="w-full h-full bg-[#374151] flex items-center justify-center">
                            <span className="text-[11px] text-white">
                              {user?.first_name.charAt(0)}
                              {user?.last_name.charAt(0)}
                            </span>
                          </div>
                        </div>
                        <span className="text-[14px] font-medium text-[#1F2937]">
                          {user?.first_name} {user?.last_name}
                        </span>
                      </div>
                      <span className="bg-[#FCE7E7] text-[#881337] text-[12px] font-medium px-3 py-1 rounded-full  ">
                        {user?.role.charAt(0).toUpperCase() + user?.role.slice(1)}
                      </span>
                    </div>
                  </>
                ))}

              {usersAssigned?.length > 2 && (
                <button
                  onClick={() => setShowEmployeesModal(true)}
                  className="w-full pt-1.5 text-[14px] font-medium text-[#881337] hover:underline"
                >
                  View Full List
                </button>
              )}
            </div>
          </div>
        </div>

        <StaffListModal
          isOpen={showEmployeesModal}
          onClose={() => setShowEmployeesModal(false)}
          users={usersAssigned}
        />

        {/* Last Updated Section */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-[#1F2937]">Last Updated</h3>
          <div className="bg-[#FEF3C7]/40 border border-[#FEF3C7] rounded-xl p-4 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-[14px] font-medium text-gray-500">
                Last Edited By:
              </span>
              <span className="text-[14px] font-medium text-[#1F2937]">
                {restaurant?.editedBy
                  ? restaurant?.editedBy?.name
                  : "N/A"}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[14px] font-medium text-gray-500">
                Last Updated On:
              </span>
              <span className="text-[14px] font-medium text-[#1F2937]">
                {new Date(restaurant.updatedAt)
                  .toLocaleString("en-US", {
                    month: "short",
                    day: "2-digit",
                    year: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                    hour12: true,
                  })
                  .replace(",", "")}
              </span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="pt-4 flex gap-2 justify-end">
          <button
            onClick={onClose}
            className="h-8 px-6 bg-white border border-gray-200 text-[#1F2937] font-medium  tracking-wider rounded-lg hover:bg-gray-50 transition-all shadow-sm text-[14px]"
          >
            Cancel
          </button>
          <Button
            className="h-8 px-6 bg-[#881337] hover:bg-[#881337] text-white font-medium  tracking-wider rounded-lg transition-all shadow-md text-[14px]"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>
    </div>
  );
}
